import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/context';
import { Icon } from '@/components/ui';
import { useDocumentKeydown } from '@/lib/useDocumentKeydown';

/** Disclosure menu with the signed-in user and sign-out. */
export function UserMenu() {
  const { user, mode, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useDocumentKeydown(open, (e) => e.key === 'Escape' && setOpen(false));

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!user) return null;

  const onSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div ref={root} className="relative min-w-0 flex-1">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-2 text-ink-soft">
          <Icon name="user" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm text-ink">{user.name}</span>
          <span className="block truncate text-xs text-ink-soft">
            {mode === 'dev' ? 'Dev bypass' : user.email}
          </span>
        </span>
      </button>
      {open && (
        <div
          id={id}
          className="absolute bottom-full left-0 mb-2 w-full rounded-md border border-line bg-surface p-1 shadow-card"
        >
          {mode === 'dev' ? (
            <p className="px-3 py-2 text-sm text-ink-soft">Sign-in is bypassed in development.</p>
          ) : (
            <button
              type="button"
              onClick={onSignOut}
              className="w-full rounded px-3 py-2 text-left text-sm text-ink hover:bg-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
            >
              Sign out
            </button>
          )}
        </div>
      )}
    </div>
  );
}
